import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Button, Input, App, Spin } from "../components/admin-ui";
import { AdminShell } from "../layouts/AdminShell";
import { useLegalDocuments, useSaveLegalDocument } from "../api/hooks";
import { MarkdownLite } from "../components/MarkdownLite";
import { LEGAL_LANGUAGES, legalLang } from "@kawkaw/shared";
import type { LegalAppScope, LegalDocType, LegalLanguage } from "@kawkaw/shared";

const APP_SCOPES: LegalAppScope[] = ["admin", "pos", "stall", "customer"];
const DOC_TYPES: LegalDocType[] = ["terms", "privacy"];

export function LegalContentScreen() {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { data, isLoading } = useLegalDocuments();
  const save = useSaveLegalDocument();

  const [appScope, setAppScope] = useState<LegalAppScope>("customer");
  const [docType, setDocType] = useState<LegalDocType>("terms");
  // start on the language the admin reads; each language is its own text, saved separately
  const [language, setLanguage] = useState<LegalLanguage>(legalLang(i18n.language));
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const docs = data?.data ?? [];
  const active = useMemo(
    () => docs.find((d) => d.app_scope === appScope && d.doc_type === docType && d.language === language) ?? null,
    [docs, appScope, docType, language],
  );
  // which languages already have a text for the chosen app + document (a dot on the tab)
  const written = useMemo(
    () => new Set(docs.filter((d) => d.app_scope === appScope && d.doc_type === docType && d.content.trim()).map((d) => d.language)),
    [docs, appScope, docType],
  );

  useEffect(() => {
    setTitle(active?.title ?? "");
    setContent(active?.content ?? "");
  }, [active]);

  const appLabel = (scope: LegalAppScope) =>
    ({ admin: t("legalContent.appAdmin"), pos: t("legalContent.appPos"), stall: t("legalContent.appStall"), customer: t("legalContent.appCustomer") }[scope]);
  const langLabel = (l: LegalLanguage) => ({ zh: "中文", en: "English", ms: "Bahasa Melayu" }[l]);
  const docLabel = (type: LegalDocType) => (type === "terms" ? t("legalContent.docTerms") : t("legalContent.docPrivacy"));

  const onSave = async () => {
    try {
      await save.mutateAsync({ appScope, docType, language, title: title.trim() || docLabel(docType), content });
      void message.success(t("legalContent.saveSuccess"));
    } catch {
      void message.error(t("legalContent.saveFailed"));
    }
  };

  return (
    <AdminShell>
      <div className="admin-legal-page admin-list-page">
        <div className="admin-legal-inner">
          <div className="admin-legal-toolbar">
            <div>
              <h1 className="admin-page-title">{t("legalContent.title")}</h1>
              <p className="admin-legal-subtitle">{t("legalContent.subtitle")}</p>
            </div>
          </div>

          {isLoading ? (
            <div className="admin-settings-loading"><Spin /></div>
          ) : (
            <>
              <div className="admin-legal-controls admin-txn-filter-panel">
                <div className="admin-legal-control-group">
                  <span className="admin-legal-control-label">{t("legalContent.applicationLabel")}</span>
                  <div className="admin-legal-tabs admin-legal-app-tabs">
                    {APP_SCOPES.map((scope) => (
                      <button key={scope} type="button" onClick={() => setAppScope(scope)} aria-pressed={appScope === scope}>
                        {appLabel(scope)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="admin-legal-control-group admin-legal-document-group">
                  <span className="admin-legal-control-label">{t("legalContent.languageLabel")}</span>
                  <div className="admin-legal-tabs admin-legal-document-tabs">
                    {LEGAL_LANGUAGES.map((l) => (
                      <button key={l} type="button" onClick={() => setLanguage(l)} aria-pressed={language === l}>
                        {langLabel(l)}{written.has(l) ? " ●" : ""}
                      </button>
                    ))}
                  </div>
                  <p className="admin-legal-empty-hint">{t("legalContent.languageHint")}</p>
                </div>
                <div className="admin-legal-control-group admin-legal-document-group">
                  <span className="admin-legal-control-label">{t("legalContent.documentLabel")}</span>
                  <div className="admin-legal-tabs admin-legal-document-tabs">
                    {DOC_TYPES.map((type) => (
                      <button key={type} type="button" onClick={() => setDocType(type)} aria-pressed={docType === type}>
                        {docLabel(type)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="admin-legal-layout">
                <Card className="admin-legal-editor admin-settings-card">
                  <div className="admin-legal-card-body">
                    <div className="admin-settings-card-heading">
                      <div>
                        <h2>{t("legalContent.editorTitle")}</h2>
                        <p>{appLabel(appScope)} · {docLabel(docType)} · {langLabel(language)}</p>
                      </div>
                    </div>
                    <label className="admin-legal-field-label">{t("legalContent.titleLabel")}</label>
                    <div className="admin-legal-title-input">
                      <Input value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} />
                    </div>

                    <label className="admin-legal-field-label">{t("legalContent.contentLabel")}</label>
                    <div className="admin-legal-content-input">
                    <Input.TextArea
                      value={content}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
                      rows={16}
                    />
                    </div>
                    {!content.trim() && <p className="admin-legal-empty-hint">{t("legalContent.unsavedContentHint")}</p>}

                    <div className="admin-legal-updated">
                      {active?.updated_at ? t("legalContent.lastUpdated", { date: new Date(active.updated_at).toLocaleString(i18n.language) }) : t("legalContent.neverUpdated")}
                    </div>

                    <div className="admin-settings-card-footer admin-legal-footer">
                    <span>{t("legalContent.saveHint")}</span>
                    <Button className="admin-legal-save" type="primary" loading={save.isPending} onClick={() => void onSave()}>
                      {t("legalContent.save")}
                    </Button>
                    </div>
                  </div>
                </Card>

                <Card className="admin-legal-preview admin-settings-card">
                  <div className="admin-legal-card-body">
                    <div className="admin-settings-card-heading admin-legal-preview-heading">
                      <div>
                        <h2>{t("legalContent.preview")}</h2>
                        <p>{t("legalContent.previewSubtitle")}</p>
                      </div>
                    </div>
                    <article className="admin-legal-preview-document">
                      <h3>{title || docLabel(docType)}</h3>
                      {content.trim() ? <MarkdownLite content={content} /> : <p className="admin-legal-preview-empty">{t("legalContent.unsavedContentHint")}</p>}
                    </article>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
